import { Download, Eye, Trash2 } from 'lucide-react';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoCategory, ProjectPhoto } from '../../types';
import { formatFileSize } from '../../utils/photoOptimizations';

interface PhotoListViewProps {
  photos: ProjectPhoto[];
  selectedPhotos: string[]; // Changed from Set to Array
  permissions: any;
  onPhotoClick: (photo: ProjectPhoto) => void;
  onSelectionToggle: (photoId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onDownload: (photo: ProjectPhoto) => void;
  onDelete: (photo: ProjectPhoto) => void;
  getCategoryInfo: (category: PhotoCategory) => { value: string; label: string; color: string };
  formatDate: (dateString: string) => string;
}

export const PhotoListView: React.FC<PhotoListViewProps> = memo(
  ({
    photos,
    selectedPhotos,
    permissions,
    onPhotoClick,
    onSelectionToggle,
    onSelectAll,
    onClearSelection,
    onDownload,
    onDelete,
    getCategoryInfo,
    formatDate,
  }) => {
    const { t } = useTranslation();

    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50/50">
              <tr className="border-b border-gray-100">
                <th className="w-12 px-4 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedPhotos.length === photos.length && photos.length > 0}
                    onChange={e => {
                      if (e.target.checked) {
                        onSelectAll();
                      } else {
                        onClearSelection();
                      }
                    }}
                    className="rounded-lg border-gray-300 text-red-500 focus:ring-red-400 h-4 w-4"
                  />
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-900">
                  {t('photos.table.photo')}
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-900 hidden lg:table-cell">
                  {t('photos.table.category')}
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-900 hidden xl:table-cell">
                  {t('photos.table.location')}
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-900 hidden md:table-cell">
                  {t('photos.table.uploaded')}
                </th>
                <th className="w-24 px-4 py-4 text-right text-sm font-semibold text-gray-900">
                  {t('photos.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {photos.map(photo => {
                const categoryInfo = getCategoryInfo(photo.category);

                return (
                  <tr
                    key={photo.photo_id}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors duration-150"
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedPhotos.includes(photo.photo_id)}
                        onChange={() => onSelectionToggle(photo.photo_id)}
                        className="rounded-lg border-gray-300 text-red-500 focus:ring-red-400 h-4 w-4"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-4">
                        <div className="h-14 w-14 rounded-xl overflow-hidden bg-gray-100 shadow-sm">
                          <img
                            src={photo.thumbnail_url || photo.file_url}
                            alt={photo.title}
                            className="h-full w-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                            onClick={() => onPhotoClick(photo)}
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate leading-5">
                            {photo.title || t('photos.untitled')}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <p className="text-xs text-gray-500">
                              {photo.file_size ? formatFileSize(photo.file_size) : ''}
                            </p>
                            {photo.number_on_image && (
                              <>
                                <span className="text-gray-300">•</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-red-50 text-red-700">
                                  #{photo.number_on_image}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${categoryInfo.color}`}
                      >
                        {categoryInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden xl:table-cell">
                      <div className="text-sm text-gray-600">
                        {photo.location?.site && (
                          <div className="font-medium">{photo.location.site}</div>
                        )}
                        {photo.location?.building && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {photo.location.building}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <div className="text-sm text-gray-600">
                        <div className="font-medium">{formatDate(photo.uploaded_at)}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => onPhotoClick(photo)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          title={t('photos.actions.view')}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {permissions.download && (
                          <button
                            onClick={() => onDownload(photo)}
                            className="p-2 text-gray-400 hover:text-brand-primary hover:bg-brand-50 rounded-lg transition-colors duration-200"
                            title={t('photos.actions.download')}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        {permissions.delete && (
                          <button
                            onClick={() => onDelete(photo)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            title={t('photos.actions.delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

PhotoListView.displayName = 'PhotoListView';
